import { useTranslation } from 'react-i18next';
import formatHelper from '@knack/format-helper';
import { type HorizontalAlignmentType } from 'recharts/types/component/DefaultLegendContent';
import { type ImplicitLabelType, type LabelPosition } from 'recharts/types/component/Label';

import { type KnackObjectResponse } from '@/types/schema/KnackObject';
import {
  type ChartCalculation,
  type ChartCalculationKey,
  type ChartData,
  type ChartLegendPosition,
  type ChartRecord
} from '@/types/schema/views/ReportView';
import {
  getSummaryFieldFormat,
  replaceNbsp
} from '@/components/views/common/table/summaries/helper';
import { type ChartCalculationByKey } from './useChart';

export const CALCULATION_PREFIX = 'calc_';
export const LIMIT_BEFORE_GROUPING = 10;

type FormatLabelValueParams = {
  value: number | string;
  chartCalculationByKeys: ChartCalculationByKey;
  calculationKey: ChartCalculationKey | undefined;
  sourceTable: KnackObjectResponse | null;
};

type GetChartLabelParams = {
  calculationKey: ChartCalculationKey | undefined;
  position: LabelPosition;
  shouldRenderChartLabel: boolean;
  chartCalculationByKeys: ChartCalculationByKey;
  sourceTable: KnackObjectResponse | null;
  fill?: string | undefined;
};

export function useChartHelpers() {
  const [t] = useTranslation();

  const aggregateData = (data: ChartRecord[]) => {
    const aggregated = new Map();

    data.forEach((item) => {
      const group = item.group_0 || t('components.views.report.others');

      // If the group doesn't exist, create it with the basic structure
      if (!aggregated.has(group)) {
        const initialValue: ChartRecord = { group_0: group };
        aggregated.set(group, initialValue);
      }

      const currentValue = aggregated.get(group)!;

      Object.keys(item).forEach((key) => {
        if (key.startsWith('calc_') || key.startsWith('raw_')) {
          // If the field doesn't exist in the accumulated value, initialize it to 0
          if (!currentValue[key]) {
            currentValue[key] = 0;
          }

          if (typeof currentValue[key] === 'number') {
            currentValue[key] = (currentValue[key] as number) + (item[key] as number);
          }
        }
      });
    });

    return Array.from(aggregated.values());
  };

  // Limits the records to a certain number of items
  const limitRecords = (data: ChartRecord[], limit = LIMIT_BEFORE_GROUPING): ChartRecord[] => {
    if (data.length <= limit) {
      return data;
    }

    const sorted = [...data].sort((a, b) => {
      const valA = typeof a.calc_0 === 'number' ? (a.calc_0 as number) : 0;
      const valB = typeof b.calc_0 === 'number' ? (b.calc_0 as number) : 0;
      return valB - valA;
    });

    const limited = sorted.slice(0, limit);

    const calcRawFields: string[] = [];
    sorted.forEach((item) => {
      Object.keys(item).forEach((key) => {
        if ((key.startsWith('calc_') || key.startsWith('raw_')) && !calcRawFields.includes(key)) {
          calcRawFields.push(key);
        }
      });
    });

    const initialOthers: ChartRecord = {
      group_0: t('components.views.report.others')
    };

    calcRawFields.forEach((field) => {
      initialOthers[field] = 0;
    });

    const others = sorted.slice(limit).reduce((acc, item) => {
      calcRawFields.forEach((field) => {
        if (typeof item[field] === 'number' && typeof acc[field] === 'number') {
          acc[field] = (acc[field] as number) + (item[field] as number);
        }
      });
      return acc;
    }, initialOthers);

    return [...limited, others];
  };

  // Get the calculation key with the maximum value. It's useful to set the YAxis domain
  const getMaxValueCalculation = (
    chartCalculations: ChartCalculation[],
    calculationRecords: ChartRecord[]
  ) => {
    let maxCalculationKey: ChartCalculationKey = `${CALCULATION_PREFIX}0`;
    let maxCalculationValue = 0;

    chartCalculations.forEach((calculation, index) => {
      const calculationKey = `${CALCULATION_PREFIX}${index}` as ChartCalculationKey;
      const calculationValues = calculationRecords.map(
        (record) => record[calculationKey] as number
      );
      const calculationMaxValue = Math.max(...calculationValues);

      if (calculationMaxValue > maxCalculationValue) {
        maxCalculationKey = calculationKey;
        maxCalculationValue = calculationMaxValue;
      }
    });

    return maxCalculationKey;
  };

  const getRecordsInPercentage = (records: ChartRecord[]) => {
    // Calculate the percentage per row only in the numeric values
    const recordsInPercentage = records.map((record) => {
      const currentRecord = { ...record };

      // Filter only the numeric values
      const calculationValues = Object.keys(currentRecord).filter(
        (key) => typeof currentRecord[key] === 'number' && key.startsWith(CALCULATION_PREFIX)
      );

      // Calculate the total sum of the numeric values
      const totalSum = calculationValues.reduce(
        (acc, key) => acc + (currentRecord[key] as number),
        0
      );

      // Calculate the percentage of each numeric value
      calculationValues.forEach((key) => {
        currentRecord[key] = (((currentRecord[key] as number) / totalSum) * 100).toFixed(2);
      });

      return currentRecord;
    });

    return recordsInPercentage;
  };

  const transformNegativeValuesToZero = (records: ChartRecord[]) =>
    records.map((record) => {
      const currentRecord = { ...record };

      Object.keys(currentRecord).forEach((key) => {
        if (
          typeof currentRecord[key] === 'number' &&
          key.startsWith(CALCULATION_PREFIX) &&
          currentRecord[key] < 0
        ) {
          currentRecord[key] = 0;
        }
      });

      return currentRecord;
    });

  // Filters the empty groups from the records
  const filterEmptyGroups = (records: ChartRecord[]) =>
    records.filter((record) => {
      const calculationValues = Object.keys(record).filter(
        (key) => typeof record[key] === 'number' && key.startsWith(CALCULATION_PREFIX)
      );

      return calculationValues.some((key) => record[key] !== null);
    });

  // Limit decimal places to 2
  const limitDecimalPlaces = (chartRecords: ChartRecord[]): ChartRecord[] => {
    if (!chartRecords.length) {
      return chartRecords;
    }

    return chartRecords.map((record) => {
      const newRecord = { ...record };

      Object.keys(record).forEach((key) => {
        if (
          (key.startsWith('calc_') || key.startsWith('raw_')) &&
          typeof record[key] === 'number'
        ) {
          newRecord[key] = parseFloat(Number(record[key]).toFixed(2));
        }
      });

      return newRecord;
    });
  };

  const hasValidData = (chartData: ChartData | undefined, chartRecords: ChartRecord[]) =>
    chartData && chartData.records && chartData.records.length > 0 && chartRecords.length > 0;

  const getLegendAlignment = (legendPosition: ChartLegendPosition): HorizontalAlignmentType => {
    if (legendPosition === 'bottom') {
      return 'center';
    }
    return legendPosition as HorizontalAlignmentType;
  };

  // This function is responsible for formatting the value if the field is of type currency or number,
  // with the format applied in the field
  const formatLabelValue = ({
    value,
    chartCalculationByKeys,
    calculationKey,
    sourceTable
  }: FormatLabelValueParams) => {
    if (!calculationKey || typeof value !== 'number') {
      return replaceNbsp(value.toString());
    }

    const currentChartCalculation = chartCalculationByKeys[calculationKey];

    const calculationField = sourceTable?.fields.find(
      (f) => f.key === currentChartCalculation.field
    );

    if (
      !calculationField ||
      (calculationField.type !== 'number' && calculationField.type !== 'currency')
    ) {
      return value.toString();
    }

    // If we are working with currency fields, it is possible that the value comes as 13&nbsp;€
    return replaceNbsp(formatHelper.formatNumber(value, getSummaryFieldFormat(calculationField)));
  };

  const getChartLabel = ({
    calculationKey,
    position,
    shouldRenderChartLabel,
    chartCalculationByKeys,
    sourceTable,
    fill
  }: GetChartLabelParams): ImplicitLabelType => {
    if (!shouldRenderChartLabel) {
      return false;
    }

    return {
      position,
      fill,
      formatter: (value: number) =>
        formatLabelValue({
          value,
          chartCalculationByKeys,
          calculationKey,
          sourceTable
        })
    };
  };

  return {
    aggregateData,
    limitRecords,
    filterEmptyGroups,
    getMaxValueCalculation,
    getRecordsInPercentage,
    transformNegativeValuesToZero,
    limitDecimalPlaces,
    getLegendAlignment,
    hasValidData,
    formatLabelValue,
    getChartLabel
  };
}
